import { call, put, takeEvery, select } from 'redux-saga/effects';
import { getType } from 'typesafe-actions';
import i18next from 'i18next';

import { RootState } from '../reducers/rootReducer';
import {
  testMobilePrinterConnection,
  testMobilePrinterConnectionSuccess,
  testMobilePrinterConnectionWarning,
  testMobilePrinterConnectionError,
} from '../actions/mobilePrinter.actions';
import { createToast } from '../actions/toasts.actions';
import { MOBILE_PRINTER_TEST_PRINT } from '../../constants';
import { ConfigAuthValues } from '../../types';
import { callAPI } from '../../utils';
import {
  parseMobilePrinterTestConnectionErrorCode,
  MOBILE_PRINTER_ERROR_CODE_INVALID_NUMBER,
  MOBILE_PRINTER_ERROR_CODE_PRINT_FAILED,
} from '../../utils/mobilePrinter';

const callTestMobilePrinterConnection = (
  storeId: string,
  printerNumber: string,
  configAuthValues: ConfigAuthValues
) => {
  const url = `${configAuthValues.APIGEE_BASE_URL}${MOBILE_PRINTER_TEST_PRINT}`.replace('{storeID}', storeId);

  return callAPI({
    method: 'POST',
    url,
    data: {
      method: 'ezcDal::testMobilePrinter',
      mobilePrinterNumber: `s:${printerNumber}`,
      textToPrint: `s:Test`,
    },
  });
};

export function* runTestMobilePrinterConnectionSaga(
  action: ReturnType<typeof testMobilePrinterConnection>
): Generator<any, void, any> {
  const configAuthValues = yield select((state: RootState) => state.config.authValues);
  const storeId = yield select((state: RootState) => state.pickerStore.storeID);

  const { printerNumber, displayToast } = action.payload;

  try {
    if (!storeId || !printerNumber) {
      throw new Error('Store ID and Printer Number are required');
    }

    const result = yield call(callTestMobilePrinterConnection, storeId, printerNumber, configAuthValues);

    if (result.status === 200) {
      yield put(testMobilePrinterConnectionSuccess());
      if (displayToast) {
        yield put(
          createToast({
            type: 'success',
            message: i18next.t('MOBILE_PRINTER.TEST_PRINT_SUCCESS'),
            duration: 5000,
          })
        );
      }
    }
  } catch (error) {
    const errorMessage = (error as any)?.response?.data?.message || (error instanceof Error ? error.message : 'Failed to test printer connection');
    const errorCode = (error as any)?.response?.data?.message ? parseMobilePrinterTestConnectionErrorCode((error as any).response.data.message) : null;


    // Only error codes 4 and 7 are relevant for the Mobile Printer page
    // Error 4: Invalid mobile printer number (must be 41-46)
    // Error 7: Printer failed to print
    if (errorCode === MOBILE_PRINTER_ERROR_CODE_INVALID_NUMBER || errorCode === MOBILE_PRINTER_ERROR_CODE_PRINT_FAILED) {
      yield put(testMobilePrinterConnectionError({ error: errorMessage, errorCode }));
    } else {
      // Fallback for unknown errors or codes 1, 2, 3, 5, 6 (shouldn't occur from UI)
      yield put(testMobilePrinterConnectionWarning());
    }
  }
}

export default function* watchTestMobilePrinterConnection() {
  yield takeEvery(getType(testMobilePrinterConnection), runTestMobilePrinterConnectionSaga);
}
